import type { LoginUserRequest } from "@/lib/api/login-user";

export interface LoginFormValues {
  email: string;
  password: string;
}

export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function isLoginFormValid(form: LoginFormValues): boolean {
  return isValidEmail(form.email.trim()) && form.password.length >= 8;
}

// "Remember me" is a client-only persistence choice (localStorage vs
// sessionStorage); the login endpoint rejects any extra field with a 400.
export function toLoginRequest(form: LoginFormValues): LoginUserRequest {
  return {
    email: form.email.trim(),
    password: form.password,
  };
}

import { API_ERROR_CODES, parseApiError } from "@/lib/api/api-error";

export const LOGIN_MESSAGES = {
  invalidCredentials: "Incorrect email or password. Please try again.",
  accountNotActive:
    "Portal account is not active yet. Please verify your email first.",
  invalidRequest: "Invalid request. Please check your credentials and try again.",
  rateLimited:
    "Too many login attempts. Please wait a few minutes before trying again.",
  accountLocked:
    "This account is temporarily locked after several failed sign-in attempts. Please wait about 15 minutes before trying again.",
  network:
    "We couldn't reach the server. Check your connection and try again.",
  generic: "Something went wrong. Please try again later.",
} as const;

export interface LoginError {
  message: string;
  // waiting is true when the user only needs to wait and retry (rate limit or
  // temporary account lockout). The UI uses it to show a "please wait" notice
  // instead of a hard error, so the user understands the account is fine.
  waiting: boolean;
  // needsVerification is true when login failed because the email is not
  // verified yet. The UI offers a resend-verification action.
  needsVerification?: boolean;
}

export function resolveLoginError(err: unknown): LoginError {
  const { status, code, message, isNetworkError } = parseApiError(err);

  if (isNetworkError) {
    return { message: LOGIN_MESSAGES.network, waiting: false };
  }

  // 429 covers both the per-IP rate limit and the per-account lockout. Prefer
  // our own copy here so the user always gets an explicit "wait" instruction
  // with a rough time estimate rather than a terse backend message.
  if (status === 429) {
    if (code === API_ERROR_CODES.loginLocked) {
      return { message: LOGIN_MESSAGES.accountLocked, waiting: true };
    }
    return { message: LOGIN_MESSAGES.rateLimited, waiting: true };
  }

  if (status === 401) {
    return {
      message: message ?? LOGIN_MESSAGES.invalidCredentials,
      waiting: false,
    };
  }
  if (status === 403 || code === API_ERROR_CODES.accountNotActive) {
    return {
      message: message ?? LOGIN_MESSAGES.accountNotActive,
      waiting: false,
      needsVerification: true,
    };
  }
  if (status === 400) {
    return { message: message ?? LOGIN_MESSAGES.invalidRequest, waiting: false };
  }

  return { message: LOGIN_MESSAGES.generic, waiting: false };
}

export function getLoginErrorMessage(err: unknown): string {
  return resolveLoginError(err).message;
}
